import { Link } from "react-router-dom";
import { Input, GoogleInput } from "@/components/ui/form";
import { Card } from "@/components/ui/card";
import { Callout } from "@/components/ui/callout";
import { Button } from "@/components/ui/button";
import { FormProvider } from "react-hook-form";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/16/solid";
import { PhoneIcon } from "@heroicons/react/24/solid";

export const PatientForm = ({ methods, patient, isPending, onSubmit }) => {
    const userEmail = patient?.email ?? "";

    return (
        <FormProvider {...methods}>
            <form
                key={patient?.id ?? "new"}
                onSubmit={methods.handleSubmit(onSubmit)}
            >
                <div className="grid grid-cols-12 gap-4">
                    <Card size="lg" className="grid-cols-12">
                        <div className="col-span-full grid grid-cols-subgrid lg:col-span-2 lg:mr-8">
                            <div className="col-span-full">
                                <h2 className="mb-3 text-xl font-semibold">
                                    Basic details
                                </h2>
                            </div>
                        </div>

                        <div className="col-span-full grid grid-cols-subgrid gap-4 md:col-start-3 md:col-end-9">
                            <div className="col-span-full grid grid-cols-12 gap-2 md:col-span-6">
                                <div className="col-span-full md:col-span-6">
                                    <Input
                                        name="email"
                                        label="Email"
                                        value={userEmail}
                                        disabled
                                    />
                                </div>
                                <Button
                                    asChild
                                    type="button"
                                    size="sm"
                                    className="mt-auto"
                                >
                                    <Link to="/patient/manage/account">
                                        <LockClosedIcon className="shrink-0" />
                                        Edit Email/Password
                                    </Link>
                                </Button>
                            </div>
                            <div className="col-span-full md:col-span-3">
                                <Input
                                    name="firstName"
                                    label="First name"
                                    placeholder="First name"
                                    autoComplete="given-name"
                                />
                            </div>
                            <div className="col-span-full md:col-span-3">
                                <Input
                                    name="lastName"
                                    label="Last name"
                                    placeholder="Last name"
                                    autoComplete="family-name"
                                />
                            </div>
                            <div className="col-span-full md:col-span-3">
                                <Input
                                    name="dateOfBirth"
                                    label="Date of birth"
                                    placeholder="Date of birth"
                                    type="date"
                                />
                            </div>
                            <div className="col-span-full md:col-span-3">
                                <Input
                                    name="phoneNumber"
                                    label="Phone number"
                                    placeholder="Phone number"
                                    icon={PhoneIcon}
                                />
                            </div>
                        </div>
                    </Card>
                    <Card size="lg" className="grid-cols-12">
                        <div className="col-span-full grid grid-cols-subgrid lg:col-span-2 lg:mr-8">
                            <div className="col-span-full">
                                <h2 className="mb-3 text-xl font-semibold">
                                    Address
                                </h2>
                            </div>
                        </div>
                        <div className="col-span-full mb-6 grid max-w-6xl grid-cols-subgrid gap-x-4 gap-y-4 lg:col-span-6">
                            <div className="col-span-full grid grid-cols-subgrid items-end">
                                <div className="col-span-full md:col-span-6 lg:col-span-3">
                                    <GoogleInput
                                        name="findAddress"
                                        label="Find Address"
                                        placeholder="Find Address"
                                        setValue={methods.setValue}
                                    />
                                </div>
                                <div className="col-span-full mt-2 md:col-span-6 md:mt-0 lg:col-span-3">
                                    <Callout variant="info" icon>
                                        <p>
                                            Autofill fields with google
                                            autocomplete
                                        </p>
                                    </Callout>
                                </div>
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressNumber"
                                    label="House number/name"
                                    placeholder="House number/name"
                                />
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressLine1"
                                    label="First line"
                                    placeholder="First line"
                                />
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressLine2"
                                    label="Second line"
                                    placeholder="Second line"
                                />
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressTown"
                                    label="Town"
                                    placeholder="Town"
                                />
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressCounty"
                                    label="County"
                                    placeholder="County"
                                    autoComplete="address-level1"
                                />
                            </div>
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="addressPostalCode"
                                    label="Postcode"
                                    placeholder="Postcode"
                                />
                            </div>
                            <div className="col-span-full mt-10 flex gap-3">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="w-fit"
                                    disabled={isPending}
                                >
                                    <CheckIcon className="stroke-2" />
                                    {isPending ? "Saving..." : "Save"}
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            </form>
        </FormProvider>
    );
};
